import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { sendNotification } from '@/lib/notifications/send-notification';

export function generateInviteCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

// ── Create class ───────────────────────────────────────────────────────────────

export type CreateClassInput = {
  tutorId: string;
  name: string;
  level: SchoolLevel | null;
  subject: SubjectSlug | null;
  description?: string;
};

export function useCreateClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateClassInput) => {
      const { data, error } = await supabase
        .from('classes')
        .insert({
          tutor_id: input.tutorId,
          name: input.name,
          level: input.level,
          subject: input.subject,
          description: input.description ?? null,
          invite_code: generateInviteCode(),
        })
        .select('id, invite_code')
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['tutor-classes', variables.tutorId] });
      qc.invalidateQueries({ queryKey: ['tutor-dashboard-stats', variables.tutorId] });
    },
  });
}

// ── Invite student ─────────────────────────────────────────────────────────────

export type InviteStudentInput = {
  tutorId: string;
  email: string;
  /** Matière(s) proposées pour ce lien (migration 0227) — vide = toutes matières. */
  subjects: SubjectSlug[];
};

export function useInviteStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: InviteStudentInput) => {
      const { data: students, error: rpcErr } = await supabase.rpc('find_student_by_email', {
        p_email: input.email.trim().toLowerCase(),
      });
      if (rpcErr) throw new Error(rpcErr.message);

      const student = (students as Array<{ id: string; display_name: string }> | null)?.[0];
      if (!student) {
        throw new Error(
          "Aucun élève Jeraisonne avec cet email. Demande à ton élève de créer son compte d'abord.",
        );
      }

      // Idempotent: reuse existing link if present, reactivate if revoked
      const { data: existing } = await supabase
        .from('tutor_links')
        .select('id, status')
        .eq('tutor_id', input.tutorId)
        .eq('student_id', student.id)
        .maybeSingle();

      if (!existing) {
        const { data, error } = await supabase
          .from('tutor_links')
          .insert({
            tutor_id: input.tutorId,
            student_id: student.id,
            status: 'pending',
            initiated_by: 'tutor',
            subjects: input.subjects,
          })
          .select('id')
          .single();
        if (error) throw new Error(error.message);
        return { link_id: data.id, student };
      }

      if (existing.status === 'revoked') {
        const { error } = await supabase
          .from('tutor_links')
          .update({ status: 'pending', initiated_by: 'tutor', subjects: input.subjects })
          .eq('id', existing.id);
        if (error) throw new Error(error.message);
      }

      return { link_id: existing.id, student };
    },
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: ['tutor-students', variables.tutorId] });
      qc.invalidateQueries({ queryKey: ['tutor-dashboard-stats', variables.tutorId] });
      sendNotification({ type: 'tutor_invite_sent', student_id: data.student.id });
    },
  });
}

// ── Invite student by ID (depuis la recherche par nom) ────────────────────────

export type InviteStudentByIdInput = {
  tutorId: string;
  studentId: string;
  /** Matière(s) proposées pour ce lien (migration 0227) — vide = toutes matières. */
  subjects: SubjectSlug[];
};

export function useInviteStudentById() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: InviteStudentByIdInput) => {
      const { data: existing } = await supabase
        .from('tutor_links')
        .select('id, status')
        .eq('tutor_id', input.tutorId)
        .eq('student_id', input.studentId)
        .maybeSingle();

      if (!existing) {
        const { data, error } = await supabase
          .from('tutor_links')
          .insert({
            tutor_id: input.tutorId,
            student_id: input.studentId,
            status: 'pending',
            initiated_by: 'tutor',
            subjects: input.subjects,
          })
          .select('id')
          .single();
        if (error) throw new Error(error.message);
        return { link_id: data.id, studentId: input.studentId };
      }

      if (existing.status === 'revoked') {
        const { error } = await supabase
          .from('tutor_links')
          .update({ status: 'pending', initiated_by: 'tutor', subjects: input.subjects })
          .eq('id', existing.id);
        if (error) throw new Error(error.message);
      }

      return { link_id: existing.id, studentId: input.studentId };
    },
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: ['tutor-students', variables.tutorId] });
      qc.invalidateQueries({ queryKey: ['tutor-dashboard-stats', variables.tutorId] });
      sendNotification({ type: 'tutor_invite_sent', student_id: data.studentId });
    },
  });
}

// ── Respond to a student-initiated request ────────────────────────────────────

export type RespondStudentRequestInput = {
  tutorId: string;
  linkId: string;
  studentId: string;
  accept: boolean;
};

export function useRespondStudentRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RespondStudentRequestInput) => {
      const { error } = await supabase
        .from('tutor_links')
        .update({
          status: input.accept ? 'active' : 'revoked',
          ...(input.accept ? { accepted_at: new Date().toISOString() } : {}),
        })
        .eq('id', input.linkId)
        .eq('tutor_id', input.tutorId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['tutor-students', variables.tutorId] });
      qc.invalidateQueries({ queryKey: ['tutor-dashboard-stats', variables.tutorId] });
      if (variables.accept) {
        sendNotification({ type: 'tutor_invite_accepted', link_id: variables.linkId });
      }
    },
  });
}

// ── Update the subject(s) of an existing link (migration 0227) ────────────────
// Ouvert au tuteur ET à l'élève : `tutor_update`/`student_update` (migration
// 0001) autorisent déjà chaque partie à modifier sa ligne sans restriction
// de colonne — aucune policy RLS nouvelle nécessaire.

export type UpdateTutorLinkSubjectsInput = {
  linkId: string;
  subjects: SubjectSlug[];
};

export function useUpdateTutorLinkSubjects(queryKeysToInvalidate: unknown[][]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateTutorLinkSubjectsInput) => {
      const { error } = await supabase
        .from('tutor_links')
        .update({ subjects: input.subjects })
        .eq('id', input.linkId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      for (const key of queryKeysToInvalidate) qc.invalidateQueries({ queryKey: key });
    },
  });
}

// ── Revoke student ─────────────────────────────────────────────────────────────

export type RevokeStudentInput = {
  tutorId: string;
  linkId: string;
};

export function useRevokeStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RevokeStudentInput) => {
      const { error } = await supabase
        .from('tutor_links')
        .update({ status: 'revoked' })
        .eq('id', input.linkId)
        .eq('tutor_id', input.tutorId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['tutor-students', variables.tutorId] });
      qc.invalidateQueries({ queryKey: ['tutor-dashboard-stats', variables.tutorId] });
    },
  });
}

// ── Remove class member ────────────────────────────────────────────────────────

export type RemoveClassMemberInput = {
  tutorId: string;
  classId: string;
  memberId: string;
};

export function useRemoveClassMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RemoveClassMemberInput) => {
      const { error } = await supabase
        .from('class_members')
        .update({ status: 'removed' })
        .eq('id', input.memberId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['tutor-class-detail', variables.classId] });
      qc.invalidateQueries({ queryKey: ['tutor-classes', variables.tutorId] });
    },
  });
}

// ── Update Anthropic API key ───────────────────────────────────────────────────

export type UpdateApiKeyInput = {
  userId: string;
  /** Nouvelle clé, ou `null` pour effacer (repli sur la clé système). */
  apiKey: string | null;
};

export function useUpdateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateApiKeyInput) => {
      // Write-only : on écrit la clé, on ne la relit jamais (migration 0190).
      // `.upsert` sans `.select()` ⇒ return=minimal, donc pas de SELECT de la
      // colonne révoquée.
      const { error } = await supabase
        .from('tutor_profiles')
        .upsert({ id: input.userId, anthropic_api_key: input.apiKey }, { onConflict: 'id' });
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['tutor-api-key-status', variables.userId] });
      qc.invalidateQueries({ queryKey: ['tutor-profile', variables.userId] });
    },
  });
}
